import { useState } from "react";
import { toast } from "sonner";
import { ScanSearch, Upload, UserSearch } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function LeakCheck() {
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [hit, setHit] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setHit(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/leak-check/reveal", fd, { responseType: "blob" });
      setImg((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
    } catch { toast.error("Couldn't read that image"); } finally { setBusy(false); }
  };
  const lookup = async () => {
    try { const r = await api.get("/admin/leak-check/lookup", { params: { code } }); setHit(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Lookup failed"); }
  };

  return (
    <section data-testid="leak-check" className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
      <div>
        <div className="flex items-center gap-2 font-bold text-slate-900"><ScanSearch size={18} /> Leak check</div>
        <p className="text-sm text-slate-500 mt-1">
          Every lesson video, Video Library video and Professor page carries the viewer's private 7-letter code, invisible to the eye.
          Upload a leaked screenshot. The code shows up as repeated "PMS XXXXXXX" text. Type it below to see who it belongs to.
        </p>
      </div>
      <label className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-6 text-sm text-slate-600 cursor-pointer hover:border-blue-400 transition-colors">
        <Upload size={16} /> {busy ? "Revealing…" : "Upload leaked screenshot (PNG/JPG)"}
        <input type="file" accept="image/*" className="hidden" data-testid="leak-check-upload" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {img && <img src={img} alt="Revealed screenshot" data-testid="leak-check-revealed" className="w-full rounded-lg border border-slate-200" />}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. K7Q3MXA" maxLength={9}
          className="w-44 font-mono tracking-widest" data-testid="leak-check-code" />
        <Button onClick={lookup} disabled={code.replace(/\W/g, "").length < 7} className="gap-1.5" data-testid="leak-check-lookup">
          <UserSearch size={15} /> Find member
        </Button>
      </div>
      {hit && (hit.found ? (
        <div data-testid="leak-check-result" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <div className="font-semibold text-amber-900">{hit.user.name} · {hit.user.email}</div>
          <div className="text-xs text-amber-800 mt-0.5">Tier: {hit.user.tier} · Code {hit.code} · Member ID {hit.user.id}</div>
        </div>
      ) : (
        <div data-testid="leak-check-none" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">No member has code {hit.code}. Double-check the letters.</div>
      ))}
    </section>
  );
}
